import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, BookOpen } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/kb")({
  head: () => ({
    meta: [
      { title: "Knowledge Base — Search articles & guides" },
      { name: "description", content: "Search our knowledge base for guides, how-tos, and answers to common questions." },
      { property: "og:title", content: "Knowledge Base" },
      { property: "og:description", content: "Guides, how-tos, and answers to common questions." },
    ],
  }),
  component: KbIndex,
});

type Category = { id: string; name: string; slug: string; sort_order: number };
type Article = {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  category_id: string | null;
  updated_at: string;
};

function KbIndex() {
  const [q, setQ] = useState("");
  const [catId, setCatId] = useState<string | null>(null);

  const { data: categories } = useQuery({
    queryKey: ["kb_categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kb_categories")
        .select("id,name,slug,sort_order")
        .order("sort_order");
      if (error) throw error;
      return data as Category[];
    },
  });

  const { data: articles, isLoading } = useQuery({
    queryKey: ["kb_articles", "public", catId, q],
    queryFn: async () => {
      let query = supabase
        .from("kb_articles")
        .select("id,title,slug,excerpt,category_id,updated_at")
        .eq("published", true)
        .order("updated_at", { ascending: false })
        .limit(50);
      if (catId) query = query.eq("category_id", catId);
      if (q.trim()) {
        const term = `%${q.trim().replace(/[%_]/g, "\\$&")}%`;
        query = query.or(`title.ilike.${term},excerpt.ilike.${term},content.ilike.${term}`);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data as Article[];
    },
  });

  const catMap = useMemo(
    () => Object.fromEntries((categories ?? []).map((c) => [c.id, c])),
    [categories],
  );

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 md:py-16">
      <div className="text-center">
        <h1 className="text-3xl font-semibold md:text-4xl">Knowledge Base</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Search guides, how-tos and answers to common questions.
        </p>
      </div>

      <div className="relative mx-auto mt-6 max-w-2xl">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search articles…"
          className="pl-10 h-12 text-base"
          maxLength={200}
        />
      </div>

      {!!categories?.length && (
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => setCatId(null)}
            className={`rounded-full border px-3 py-1 text-xs transition ${
              catId === null ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setCatId(c.id)}
              className={`rounded-full border px-3 py-1 text-xs transition ${
                catId === c.id ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}

      <div className="mt-10">
        {isLoading ? (
          <div className="text-center text-sm text-muted-foreground">Loading…</div>
        ) : !articles?.length ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border p-10 text-center">
            <BookOpen className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {q ? "No articles match your search." : "No articles published yet."}
            </p>
          </div>
        ) : (
          <ul className="grid gap-3 md:grid-cols-2">
            {articles.map((a) => {
              const cat = a.category_id ? catMap[a.category_id] : null;
              return (
                <li key={a.id}>
                  <Link
                    to="/kb/$slug"
                    params={{ slug: a.slug }}
                    className="block rounded-lg border border-border bg-card p-4 transition hover:border-primary/40 hover:bg-card/80"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h2 className="text-base font-medium">{a.title}</h2>
                      {cat && <Badge variant="outline" className="shrink-0">{cat.name}</Badge>}
                    </div>
                    {a.excerpt && (
                      <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{a.excerpt}</p>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
